import React, { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { UserRejectedRequestError } from 'viem';
import type { Connector } from 'wagmi';
import { useConnect } from 'wagmi';

import { ReactComponent as XIcon } from '../../../../common/svg/general/icon-x.svg';
import { useDisconnect, useWeb3State } from '../../../../hooks';
import { WalletButton } from './blocks';
import { WALLETS_META_MAP } from './constants';
import styles from './styles.module.css';

interface Props {
  hideModal: () => void;
  onConnect?: (address: string) => Promise<void> | void;
}

const Modal = ({ hideModal, onConnect }: Props) => {
  const { walletDetails, connect } = useWeb3State();
  const { connectors, connectAsync } = useConnect();
  const { disconnectAsync } = useDisconnect();

  const nonInjectedConnectors = useMemo(
    () => connectors.filter(c => c.id !== 'injected'),
    [connectors],
  );

  const handleConnect = useCallback(
    (connector: Connector) => async () => {
      try {
        await disconnectAsync();

        console.debug({ connector });

        const account = await connect(connector);

        hideModal();

        if (!account || !onConnect) {
          return;
        }

        void onConnect(account);
      } catch (error) {
        if (error instanceof UserRejectedRequestError) {
          toast.error('User rejected request');
        } else if (error instanceof Error) {
          toast.error(error.message);
        }

        console.error(error);
      }
    },
    [
      connect,
      connectAsync,
      disconnectAsync,
      onConnect,
      hideModal,
      walletDetails.chain.id,
      walletDetails.chain.key,
    ],
  );

  return (
    <div className={styles.modal}>
      <div className={styles.header}>
        <h1>Connect your wallet</h1>
        <button onClick={hideModal}>
          <XIcon />
        </button>
      </div>
      <div className={styles.buttons}>
        {nonInjectedConnectors.map(c => (
          <WalletButton
            key={c.id}
            meta={WALLETS_META_MAP[c.id]}
            isDisabled={!c.ready}
            onConnect={handleConnect(c)}
            className={c.id}
          />
        ))}
      </div>
    </div>
  );
};

export const ConnectWalletModal =
  (onConnect?: Props['onConnect']) =>
  ({ hideModal }: Props) => {
    return <Modal hideModal={hideModal} onConnect={onConnect} />;
  };
